import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { usersApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardBody } from '@/components/ui/card';

export function ProfilePage() {
  const { user, updateUser } = useAuth();

  // ── Account info form ──────────────────────────────────────────────────────
  const [fullName, setFullName]       = useState(user?.full_name ?? '');
  const [infoSaved, setInfoSaved]     = useState(false);

  const saveInfo = useMutation({
    mutationFn: () => usersApi.updateMe({ full_name: fullName }),
    onSuccess: (updated) => {
      updateUser({ full_name: updated.full_name });
      setInfoSaved(true);
      setTimeout(() => setInfoSaved(false), 3000);
    },
  });

  // ── Notification preferences ────────────────────────────────────────────────
  // Default to on for sessions stored before this field existed.
  const emailNotifOn = user?.email_notifications ?? true;
  const [notifSaved, setNotifSaved] = useState(false);

  const saveNotif = useMutation({
    mutationFn: (next: boolean) => usersApi.updateMe({ email_notifications: next }),
    onSuccess: (updated) => {
      updateUser({ email_notifications: updated.email_notifications });
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 3000);
    },
  });

  // ── Password form ──────────────────────────────────────────────────────────
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwSaved, setPwSaved]       = useState(false);
  const [pwError, setPwError]       = useState('');

  const savePw = useMutation({
    mutationFn: () => usersApi.updateMe({ current_password: currentPw, new_password: newPw }),
    onSuccess: () => {
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setPwError('');
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    },
    onError: (err: Error) => setPwError(err.message),
  });

  function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    if (newPw !== confirmPw) { setPwError('New passwords do not match.'); return; }
    if (newPw.length < 8)    { setPwError('New password must be at least 8 characters.'); return; }
    savePw.mutate();
  }

  return (
    <div className="max-w-xl flex flex-col gap-5">

      {/* ── Account Info ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title="Account Info" />
        <CardBody>
          <form
            onSubmit={e => { e.preventDefault(); saveInfo.mutate(); }}
            className="flex flex-col gap-4"
          >
            <Input
              label="Email"
              value={user?.email ?? ''}
              disabled
              className="bg-slate-50 text-slate-400"
            />
            <div>
              <p className="text-xs text-slate-500 mb-1">Role</p>
              <p className="text-sm font-medium text-slate-700 capitalize">{user?.role}</p>
            </div>
            <Input
              label="Full Name"
              value={fullName}
              onChange={e => { setFullName(e.target.value); setInfoSaved(false); }}
              required
            />
            {saveInfo.error && (
              <p className="text-sm text-red-600">{(saveInfo.error as Error).message}</p>
            )}
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={saveInfo.isPending || fullName === user?.full_name}>
                {saveInfo.isPending ? 'Saving…' : 'Save Name'}
              </Button>
              {infoSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle2 size={15} /> Saved
                </span>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      {/* ── Notifications ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title="Notifications" />
        <CardBody>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Email notifications</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Also send your notifications to email. In-app notifications stay on either way.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={emailNotifOn}
                disabled={saveNotif.isPending}
                onChange={e => saveNotif.mutate(e.target.checked)}
              />
              <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-brand-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
            </label>
          </div>
          {saveNotif.error && (
            <p className="text-sm text-red-600 mt-2">{(saveNotif.error as Error).message}</p>
          )}
          {notifSaved && (
            <span className="flex items-center gap-1 text-sm text-green-600 mt-2">
              <CheckCircle2 size={15} /> Saved
            </span>
          )}
        </CardBody>
      </Card>

      {/* ── Change Password ──────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title="Change Password" />
        <CardBody>
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
            <Input
              label="Current Password"
              type="password"
              value={currentPw}
              onChange={e => { setCurrentPw(e.target.value); setPwError(''); }}
              required
              autoComplete="current-password"
            />
            <Input
              label="New Password"
              type="password"
              value={newPw}
              onChange={e => { setNewPw(e.target.value); setPwError(''); }}
              required
              autoComplete="new-password"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPw}
              onChange={e => { setConfirmPw(e.target.value); setPwError(''); }}
              required
              autoComplete="new-password"
            />
            {pwError && <p className="text-sm text-red-600">{pwError}</p>}
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={savePw.isPending || !currentPw || !newPw || !confirmPw}>
                {savePw.isPending ? 'Updating…' : 'Update Password'}
              </Button>
              {pwSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle2 size={15} /> Password updated
                </span>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

    </div>
  );
}
